// SOURCE OF TRUTH: supabase/functions/_shared/legal-vocabulary.ts
//
// A maintained MIRROR, for the same reason src/lib/autonomy.ts is one: the
// shared module is Deno code the Vite bundle can never import. Every function
// below must behave identically to its twin — pinned across a matrix of
// jurisdictions and fallbacks by tests/unit/legal-vocabulary-parity.test.mjs,
// which imports BOTH and compares outputs. Read the twin for the full rationale
// (grammar constraint, and why state statutes are withheld rather than
// substituted); it is not repeated here so the two cannot disagree in prose.

/** What stands in for the state name before an admin has picked one. */
export const STATE_FALLBACK = 'your state'

/** Normalize a display name from `jurisdictions.name` (mig 220). */
export function stateNameOr(stateName: string | null | undefined): string {
  return stateName?.trim() ? stateName.trim() : STATE_FALLBACK
}

/** "a licensed real estate attorney in Idaho" — always trailing. */
export function attorneyPhrase(stateName: string | null | undefined): string {
  return `a licensed real estate attorney in ${stateNameOr(stateName)}`
}

/** "a licensed attorney in Idaho" — general counsel, matching mig 221. */
export function generalAttorneyPhrase(stateName: string | null | undefined): string {
  return `a licensed attorney in ${stateNameOr(stateName)}`
}

/** "a licensed real estate professional in Idaho" — the FHA review referral. */
export function realEstateProfessionalPhrase(stateName: string | null | undefined): string {
  return `a licensed real estate professional in ${stateNameOr(stateName)}`
}

/** Federal always; Idaho Code 55-616 only on Idaho. */
export function fairHousingAuthority(jurisdictionCode: string | null | undefined): string {
  const federal = 'the Fair Housing Act (42 U.S.C. 3601 et seq.)'
  return jurisdictionCode === 'ID' ? `${federal} and Idaho Code 55-616` : federal
}

/** The notice shown in place of an answer that may quote a protected class. */
export function fairHousingWithheldNotice(
  stateName: string | null | undefined,
  jurisdictionCode: string | null | undefined,
): string {
  return (
    'The documents on file contain a provision that may reference a protected class under ' +
    'the Fair Housing Act. This response has been withheld pending review by ' +
    `${realEstateProfessionalPhrase(stateName)}. Discriminatory covenants are void and ` +
    `unenforceable under ${fairHousingAuthority(jurisdictionCode)}.`
  )
}

/** Under which law an agent is personally responsible for what they send. */
export function licenseLawAuthority(
  jurisdictionCode: string | null | undefined,
  stateName: string | null | undefined,
): string {
  if (jurisdictionCode === 'ID') return 'Idaho Code 54-2060'
  const name = stateName?.trim()
  return name ? `${name} real estate license law` : "your state's real estate license law"
}

// ── who handles title and closing, in all 51 jurisdictions ──────────────────
// Authorities per entry live in the twin. Sourced from
// docs/scopes/2026-07-27-multi-state-jurisdiction-research.md §3 — never edit a
// tier from a summary or from memory; that survey caught seven published lists
// over-reporting toward "attorney required".

/** Tier 1 — an attorney must conduct the closing itself (research §3.2). */
const ATTORNEY_CLOSING_STATES: readonly string[] = ['CT', 'DE', 'GA', 'MA', 'NC', 'SC', 'WV']

/** Tier 2 — an attorney is reserved for ONE act; closings stay open (§3.3). */
const ATTORNEY_ACT_RESTRICTED_STATES: readonly string[] = [
  'AL', 'AR', 'IA', 'IL', 'IN', 'MD', 'MO', 'MS', 'ND', 'OH', 'OK', 'RI', 'WI',
]

export type ClosingAttorneyTier = 'attorney_closing' | 'act_restricted' | 'none'

export function closingAttorneyTier(jurisdictionCode: string | null | undefined): ClosingAttorneyTier {
  const code = jurisdictionCode?.trim().toUpperCase()
  if (!code) return 'none'
  if (ATTORNEY_CLOSING_STATES.includes(code)) return 'attorney_closing'
  if (ATTORNEY_ACT_RESTRICTED_STATES.includes(code)) return 'act_restricted'
  return 'none'
}

export interface TitleVocabulary {
  /** Fits: "escalate to ___ for factual title questions". */
  escalateTo: string
  /** Fits: "always verify document currency with ___". */
  verifyWith: string
}

/** Iowa bans title insurance (Iowa Code § 515.48(10)) — the only entry that differs. */
export function titleVocabulary(jurisdictionCode: string | null | undefined): TitleVocabulary {
  if (jurisdictionCode?.trim().toUpperCase() === 'IA') {
    return {
      escalateTo: 'an attorney or Iowa Title Guaranty',
      verifyWith: 'the county recorder, Iowa Title Guaranty, or an attorney',
    }
  }
  return {
    escalateTo: 'the title company',
    verifyWith: 'the county recorder or your title company',
  }
}

/** Louisiana runs on civil-law notaries (La. R.S. 35:2), not closing agents. */
export function closingProfessionalPhrase(jurisdictionCode: string | null | undefined): string {
  const code = jurisdictionCode?.trim().toUpperCase()
  if (code === 'LA') return 'a notary'
  return closingAttorneyTier(code) === 'attorney_closing' ? 'a closing attorney' : 'your closing agent'
}

export interface HoaStatuteCaveat {
  /** The whole sentence, already resolved. */
  text: string
  /** The exact substring the UI emphasises, or null when nothing is named. */
  emphasize: string | null
}

/** The HOA-statute caveat under CC&R content. */
export function hoaStatuteCaveat(
  jurisdictionCode: string | null | undefined,
  stateName: string | null | undefined,
): HoaStatuteCaveat {
  const referral = `Consult ${generalAttorneyPhrase(stateName)} to determine how it applies to a specific provision.`
  if (jurisdictionCode === 'ID') {
    const actName = "Idaho Homeowner's Association Act"
    return {
      text:
        `Some provisions in these documents may be limited or superseded under the ${actName} ` +
        `(Idaho Code Title 55, Chapter 32). ${referral}`,
      emphasize: actName,
    }
  }
  return {
    text:
      'Some provisions in these documents may be limited or superseded by state ' +
      `homeowners' association law. ${referral}`,
    emphasize: null,
  }
}

/** True when a literal template token survived resolution — used by the pins. */
export function hasUnresolvedToken(text: string): boolean {
  return text.includes('{{')
}
